import { Signal } from '@angular/core';

/**
 * Abstract contract for any filing-form loading-UX implementation.
 *
 * The container (and any other caller) depends on this abstraction via the
 * `FILING_LOADER_STRATEGY` injection token — never on a concrete class. This
 * keeps the call sites free of mode-awareness: swapping the active variant is
 * a single provider decision in the composition root.
 *
 * Each subclass is paired with an optional UI component via the registry at
 * `filing-loader.registry.ts`. UI components read from the injected strategy
 * instance through this same abstract surface; they never import a concrete
 * strategy class either.
 *
 * @see docs/ui/loaders/filing/README.md
 */
export abstract class FilingLoaderStrategy {
  /** Whether a load is currently active (drives UI visibility gates). */
  abstract readonly isActive: Signal<boolean>;

  /** Human-readable phase label (e.g. "Verifying access..."). */
  abstract readonly phaseLabel: Signal<string>;

  /** Current sheet being hydrated, or `null` when outside the sheet loop. */
  abstract readonly currentSheetLabel: Signal<string | null>;

  /** Number of sheets completed so far in the current hydration pass. */
  abstract readonly completedCount: Signal<number>;

  /** Total sheets expected for the current hydration pass. */
  abstract readonly totalCount: Signal<number>;

  /** Elapsed milliseconds since the most recent `begin()`. */
  abstract readonly elapsedMs: Signal<number>;

  /**
   * Final elapsed duration of the most recently completed load, in ms. `null`
   * when no load has completed since the last `begin()` or since the last
   * `acknowledgeCompletion()` call. Drives any "load complete" UI affordance.
   */
  abstract readonly lastCompletedElapsedMs: Signal<number | null>;

  /**
   * Start a new load-progress window. Must be safe to call when one is
   * already active (implementations should reset counters and restart).
   */
  abstract begin(initialPhase?: string): void;

  /** Update the top-line phase message for coarse transitions. */
  abstract reportPhase(label: string): void;

  /** Report progress within the per-sheet hydration loop. */
  abstract reportSheet(sheetLabel: string, completed: number, total: number): void;

  /**
   * End the load-progress window. Must be idempotent — safe to call from a
   * `finalize()` block that may run multiple times on edge paths.
   */
  abstract end(): void;

  /** Clear the completion snapshot (dismiss any "load complete" UI). */
  abstract acknowledgeCompletion(): void;
}
